import { useState, useEffect } from 'react'
import { Card, Table, Badge, Button, Row, Col, Spinner, Alert, Modal, Form, InputGroup } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useClientFilter } from '../../hooks/useClientFilter'
import api from '../../services/api'
import { formatDateOnlyEsMX, getTodayDateOnlyMx, startOfLocalDayFromApi } from '../../utils/apiDateFormat'
import { formatCurrency } from '../../utils/formatters'

const CollectionsDashboard = () => {
  const { user } = useAuth()
  const { selectedClientId, isFiltering } = useClientFilter()
  const queryClient = useQueryClient()
  const [selectedCobranza, setSelectedCobranza] = useState(null)
  const [showSeguimientoModal, setShowSeguimientoModal] = useState(false)
  const [showIdentificarModal, setShowIdentificarModal] = useState(false)
  const [filtros, setFiltros] = useState({
    cliente_id: '',
    prioridad: '',
    estado: ''
  })
  
  // Formulario de seguimiento
  const [seguimientoForm, setSeguimientoForm] = useState({
    fecha: getTodayDateOnlyMx(),
    tipo_contacto: 'llamada',
    descripcion: '',
    compromiso_pago: '',
    monto_comprometido: ''
  })
  
  // Formulario de identificación
  const [identificarForm, setIdentificarForm] = useState({
    cliente_id: '',
    dias_vencido_minimo: 0
  })

  useEffect(() => {
    setFiltros(prev => ({
      ...prev,
      cliente_id: selectedClientId != null ? String(selectedClientId) : '',
    }))
  }, [selectedClientId])

  // Construir query params para filtros
  const buildQueryParams = () => {
    const params = new URLSearchParams()
    if (filtros.cliente_id) params.append('cliente_id', filtros.cliente_id)
    if (filtros.prioridad) params.append('prioridad', filtros.prioridad)
    if (filtros.estado) params.append('estado', filtros.estado)
    return params.toString()
  }

  // Obtener cobranzas con filtros
  const { data: cobranzas, isLoading } = useQuery(
    ['collections', filtros],
    async () => {
      const queryParams = buildQueryParams()
      const url = queryParams ? `/financial/cobranza/?${queryParams}` : '/financial/cobranza/'
      const response = await api.get(url)
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Obtener seguimientos desde el objeto cobranza o endpoint separado
  const seguimientos = selectedCobranza 
    ? cobranzas?.find(c => c.id === selectedCobranza)?.seguimientos || []
    : []

  const getEstadoBadge = (estado) => {
    const badges = {
      'pendiente': { bg: 'info', text: 'Pendiente' },
      'en_seguimiento': { bg: 'warning', text: 'En Seguimiento' },
      'pagada': { bg: 'success', text: 'Pagada' },
      'cancelada': { bg: 'danger', text: 'Cancelada' }
    }
    const badge = badges[estado] || { bg: 'secondary', text: estado }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const getPrioridadBadge = (prioridad) => {
    const badges = {
      'baja': { bg: 'info', text: 'Baja' },
      'media': { bg: 'warning', text: 'Media' },
      'alta': { bg: 'danger', text: 'Alta' },
      'critica': { bg: 'danger', text: 'Crítica' }
    }
    const badge = badges[prioridad] || { bg: 'secondary', text: prioridad }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const getDiasVencido = (fechaVencimiento) => {
    const hoy = new Date()
    const vencimiento = new Date(fechaVencimiento)
    const diffTime = hoy - vencimiento
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays > 0 ? diffDays : 0
  }

  // Obtener clientes para filtros
  const { data: clients } = useQuery(
    'clients-for-collections',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    }
  )

  // Mutación para agregar seguimiento
  const addSeguimientoMutation = useMutation(
    async (data) => {
      const response = await api.post(`/financial/cobranza/${selectedCobranza}/seguimiento/`, data)
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Seguimiento agregado exitosamente')
        setShowSeguimientoModal(false)
        setSeguimientoForm({
          fecha: getTodayDateOnlyMx(),
          tipo_contacto: 'llamada',
          descripcion: '',
          compromiso_pago: '',
          monto_comprometido: ''
        })
        queryClient.invalidateQueries('collections')
      },
      onError: (error) => {
        const message = error.response?.data?.error || error.response?.data?.message || 'Error al agregar seguimiento'
        toast.error(message)
      }
    }
  )

  // Mutación para identificar facturas vencidas
  const identificarVencidasMutation = useMutation(
    async (data) => {
      const response = await api.post('/financial/cobranza/identificar_vencidas/', data)
      return response.data
    },
    {
      onSuccess: (data) => {
        const count = Array.isArray(data) ? data.length : (data.results?.length || 0)
        toast.success(`${count} factura(s) vencida(s) identificada(s)`)
        setShowIdentificarModal(false)
        setIdentificarForm({
          cliente_id: '',
          dias_vencido_minimo: 0
        })
        queryClient.invalidateQueries('collections')
      },
      onError: (error) => {
        const message = error.response?.data?.error || error.response?.data?.message || 'Error al identificar facturas vencidas'
        toast.error(message)
      }
    }
  )

  // Calcular estadísticas
  const totalCobranzas = cobranzas?.length || 0
  const totalMonto = cobranzas?.reduce((sum, c) => sum + parseFloat(c.monto_pendiente || 0), 0) || 0
  const promedioDias = cobranzas?.length > 0
    ? Math.round(cobranzas.reduce((sum, c) => sum + getDiasVencido(c.fecha_vencimiento), 0) / cobranzas.length)
    : 0

  // Obtener cobranza seleccionada para validaciones
  const cobranzaSeleccionada = cobranzas?.find(c => c.id === selectedCobranza)

  // Manejar envío de seguimiento
  const handleSubmitSeguimiento = (e) => {
    e.preventDefault()
    
    // Validaciones
    if (!seguimientoForm.descripcion.trim()) {
      toast.error('La descripción es requerida')
      return
    }

    if (seguimientoForm.compromiso_pago) {
      const compromisoDate = startOfLocalDayFromApi(seguimientoForm.compromiso_pago)
      const hoy = startOfLocalDayFromApi(getTodayDateOnlyMx())
      if (compromisoDate && hoy && compromisoDate < hoy) {
        toast.error('El compromiso de pago debe ser una fecha futura')
        return
      }
    }

    if (seguimientoForm.monto_comprometido) {
      const monto = parseFloat(seguimientoForm.monto_comprometido)
      const montoPendiente = parseFloat(cobranzaSeleccionada?.monto_pendiente || 0)
      if (monto > montoPendiente) {
        toast.error(`El monto comprometido no puede exceder el monto pendiente (${formatCurrency(montoPendiente)})`)
        return
      }
      if (monto <= 0) {
        toast.error('El monto comprometido debe ser mayor a 0')
        return
      }
    }

    const data = {
      fecha: seguimientoForm.fecha,
      tipo_contacto: seguimientoForm.tipo_contacto,
      descripcion: seguimientoForm.descripcion,
      compromiso_pago: seguimientoForm.compromiso_pago || null,
      monto_comprometido: seguimientoForm.monto_comprometido ? parseFloat(seguimientoForm.monto_comprometido) : null
    }

    addSeguimientoMutation.mutate(data)
  }

  // Manejar identificación de vencidas
  const handleIdentificarVencidas = (e) => {
    e.preventDefault()
    const data = {
      cliente_id: identificarForm.cliente_id || null,
      dias_vencido_minimo: parseInt(identificarForm.dias_vencido_minimo) || 0
    }
    identificarVencidasMutation.mutate(data)
  }

  // Limpiar filtros
  const handleClearFilters = () => {
    setFiltros({
      cliente_id: '',
      prioridad: '',
      estado: ''
    })
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Dashboard de Cobranza</h3>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={() => setShowIdentificarModal(true)}
          >
            Identificar Facturas Vencidas
          </Button>
        </div>
      </div>

      {/* Estadísticas */}
      <Row className="mb-4">
        <Col md={4}>
          <Card>
            <Card.Body>
              <Card.Title className="text-muted small">Total Cobranzas</Card.Title>
              <h3>{totalCobranzas}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <Card.Title className="text-muted small">Monto Total Vencido</Card.Title>
              <h3>
                {formatCurrency(totalMonto)}
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <Card.Title className="text-muted small">Promedio Días Vencido</Card.Title>
              <h3>{promedioDias} días</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Lista de cobranzas */}
      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : (
        <Card>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Facturas Vencidas</h5>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleClearFilters}
                disabled={!filtros.cliente_id && !filtros.prioridad && !filtros.estado}
              >
                Limpiar Filtros
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            {/* Filtros */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Cliente</Form.Label>
                  <Form.Select
                    value={filtros.cliente_id}
                    onChange={(e) => setFiltros({ ...filtros, cliente_id: e.target.value })}
                    disabled={isFiltering}
                  >
                    <option value="">Todos los clientes</option>
                    {clients?.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.razon_social || client.business_name || `${client.nombre || ''} ${client.apellido_paterno || ''}`.trim()}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Prioridad</Form.Label>
                  <Form.Select
                    value={filtros.prioridad}
                    onChange={(e) => setFiltros({ ...filtros, prioridad: e.target.value })}
                  >
                    <option value="">Todas las prioridades</option>
                    <option value="baja">Baja</option>
                    <option value="media">Media</option>
                    <option value="alta">Alta</option>
                    <option value="critica">Crítica</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    value={filtros.estado}
                    onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
                  >
                    <option value="">Todos los estados</option>
                    <option value="pendiente">Pendiente</option>
                    <option value="en_seguimiento">En Seguimiento</option>
                    <option value="pagada">Pagada</option>
                    <option value="cancelada">Cancelada</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            {cobranzas && cobranzas.length > 0 ? (
              <Table responsive>
                <thead>
                  <tr>
                    <th>Factura</th>
                    <th>Cliente</th>
                    <th>Receptor</th>
                    <th>Fecha Vencimiento</th>
                    <th>Días Vencido</th>
                    <th>Monto Pendiente</th>
                    <th>Prioridad</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cobranzas.map((cobranza) => {
                    const diasVencido = getDiasVencido(cobranza.fecha_vencimiento)
                    return (
                      <tr key={cobranza.id}>
                        <td>
                          {cobranza.factura_number || cobranza.xmlcfdi_uuid || 'N/A'}
                          {cobranza.tipo_fuente === 'contabilidad' && (
                            <Badge bg="secondary" className="ms-2">XML</Badge>
                          )}
                        </td>
                        <td>{cobranza.cliente_name || 'N/A'}</td>
                        <td>{cobranza.receptor_name || 'N/A'}</td>
                        <td>{cobranza.fecha_vencimiento ? formatDateOnlyEsMX(cobranza.fecha_vencimiento, {}, 'N/A') : 'N/A'}</td>
                        <td>
                          <Badge bg={diasVencido > 30 ? 'danger' : diasVencido > 15 ? 'warning' : 'info'}>
                            {diasVencido} días
                          </Badge>
                        </td>
                        <td>
                          {formatCurrency(cobranza.monto_pendiente || 0)}
                        </td>
                        <td>{getPrioridadBadge(cobranza.prioridad)}</td>
                        <td>{getEstadoBadge(cobranza.estado)}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setSelectedCobranza(cobranza.id)}
                            >
                              Ver Seguimiento
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => {
                                setSelectedCobranza(cobranza.id)
                                setShowSeguimientoModal(true)
                              }}
                            >
                              Agregar Seguimiento
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            ) : (
              <p className="text-muted">No hay facturas vencidas</p>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Panel de seguimiento */}
      {selectedCobranza && (
        <Card className="mt-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Seguimiento de Cobranza</h5>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setSelectedCobranza(null)}
            >
              Cerrar
            </Button>
          </Card.Header>
          <Card.Body>
            {seguimientos && seguimientos.length > 0 ? (
              <Table responsive>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo de Contacto</th>
                    <th>Descripción</th>
                    <th>Compromiso de Pago</th>
                    <th>Monto Comprometido</th>
                    <th>Usuario</th>
                  </tr>
                </thead>
                <tbody>
                  {seguimientos.map((seguimiento) => (
                    <tr key={seguimiento.id}>
                      <td>{formatDateOnlyEsMX(seguimiento.fecha, {}, '—')}</td>
                      <td>
                        <Badge bg="info">
                          {seguimiento.tipo_contacto === 'llamada' ? '📞 Llamada' :
                           seguimiento.tipo_contacto === 'email' ? '📧 Email' :
                           seguimiento.tipo_contacto === 'visita' ? '👤 Visita' :
                           seguimiento.tipo_contacto === 'nota' ? '📝 Nota' :
                           seguimiento.tipo_contacto}
                        </Badge>
                      </td>
                      <td>{seguimiento.descripcion || 'N/A'}</td>
                      <td>
                        {seguimiento.compromiso_pago
                          ? formatDateOnlyEsMX(seguimiento.compromiso_pago, {}, 'N/A')
                          : 'N/A'}
                      </td>
                      <td>
                        {seguimiento.monto_comprometido
                          ? `${formatCurrency(seguimiento.monto_comprometido)}`
                          : 'N/A'}
                      </td>
                      <td>{seguimiento.created_by_username || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p className="text-muted">No hay seguimientos registrados</p>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Modal para agregar seguimiento */}
      <Modal show={showSeguimientoModal} onHide={() => setShowSeguimientoModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Agregar Seguimiento de Cobranza</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitSeguimiento}>
          <Modal.Body>
            {cobranzaSeleccionada && (
              <Alert variant="info" className="mb-3">
                <strong>Factura:</strong> {cobranzaSeleccionada.factura_number}<br />
                <strong>Cliente:</strong> {cobranzaSeleccionada.cliente_name}<br />
                <strong>Monto Pendiente:</strong> {formatCurrency(cobranzaSeleccionada.monto_pendiente || 0)}
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Fecha *</Form.Label>
              <Form.Control
                type="date"
                value={seguimientoForm.fecha}
                onChange={(e) => setSeguimientoForm({ ...seguimientoForm, fecha: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tipo de Contacto *</Form.Label>
              <Form.Select
                value={seguimientoForm.tipo_contacto}
                onChange={(e) => setSeguimientoForm({ ...seguimientoForm, tipo_contacto: e.target.value })}
                required
              >
                <option value="llamada">📞 Llamada</option>
                <option value="email">📧 Email</option>
                <option value="visita">👤 Visita</option>
                <option value="nota">📝 Nota</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descripción *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={seguimientoForm.descripcion}
                onChange={(e) => setSeguimientoForm({ ...seguimientoForm, descripcion: e.target.value })}
                placeholder="Describe el contacto realizado..."
                required
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Compromiso de Pago</Form.Label>
                  <Form.Control
                    type="date"
                    value={seguimientoForm.compromiso_pago}
                    onChange={(e) => setSeguimientoForm({ ...seguimientoForm, compromiso_pago: e.target.value })}
                    min={getTodayDateOnlyMx()}
                  />
                  <Form.Text className="text-muted">
                    Fecha acordada para el pago (opcional)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Monto Comprometido</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={seguimientoForm.monto_comprometido}
                      onChange={(e) => setSeguimientoForm({ ...seguimientoForm, monto_comprometido: e.target.value })}
                      placeholder="0.00"
                    />
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Monto que el cliente se comprometió a pagar (opcional)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSeguimientoModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={addSeguimientoMutation.isLoading}>
              {addSeguimientoMutation.isLoading ? 'Guardando...' : 'Guardar Seguimiento'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para identificar facturas vencidas */}
      <Modal show={showIdentificarModal} onHide={() => setShowIdentificarModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Identificar Facturas Vencidas</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleIdentificarVencidas}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Cliente</Form.Label>
              <Form.Select
                value={identificarForm.cliente_id}
                onChange={(e) => setIdentificarForm({ ...identificarForm, cliente_id: e.target.value })}
              >
                <option value="">Todos los clientes</option>
                {clients?.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.razon_social || client.business_name || `${client.nombre || ''} ${client.apellido_paterno || ''}`.trim()}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Dejar vacío para identificar facturas de todos los clientes
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Días Vencidos Mínimos</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={identificarForm.dias_vencido_minimo}
                onChange={(e) => setIdentificarForm({ ...identificarForm, dias_vencido_minimo: e.target.value })}
              />
              <Form.Text className="text-muted">
                Solo identificar facturas con al menos esta cantidad de días vencidos (default: 0)
              </Form.Text>
            </Form.Group>
            <Alert variant="info">
              El sistema identificará facturas con estado &quot;Enviada&quot; o &quot;Parcialmente Pagada&quot; que tengan fecha de vencimiento anterior a hoy.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowIdentificarModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={identificarVencidasMutation.isLoading}>
              {identificarVencidasMutation.isLoading ? 'Identificando...' : 'Identificar Facturas'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

export default CollectionsDashboard

